/**
 * BodyweightCard — collapsible, single-purpose bodyweight (lbs) input.
 *
 * Used at the top of athlete workout runner, coach workout runner, and the
 * coach athlete dossier. Backs onto the existing /api/me/biometrics
 * endpoint, which:
 *   - resolves the active athlete (or the kid a parent is running through),
 *   - lets owner/coach override anyone's biometrics via ?athlete_id=...,
 *   - appends to `biometrics_history` so we keep a timestamped trail
 *     (every save creates a new history row).
 *
 * Bodyweight feeds progression chains that gate on % of bodyweight.
 * Owner spec 2026-02-28: lbs only, collapsible, accessible anywhere a
 * workout is run or an athlete's file is open.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Scale, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatAgo(iso) {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    const y = Math.floor(mo / 12);
    return `${y}y ago`;
}

export default function BodyweightCard({
    athleteId,
    athleteName,
    defaultOpen = false,
    className = "",
}) {
    const [open, setOpen] = useState(defaultOpen);
    const [weight, setWeight] = useState("");
    const [serverValue, setServerValue] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const params = athleteId ? { athlete_id: athleteId } : {};
                const r = await axios.get(`${API}/me/biometrics`, {
                    params,
                    withCredentials: true,
                });
                if (cancel) return;
                const w = r.data?.weight_lbs;
                setServerValue(w ?? null);
                setWeight(w != null ? String(w) : "");
                setUpdatedAt(r.data?.updated_at || null);
                // Auto-expand on first load if it's never been set — surfaces
                // the prompt instead of hiding the empty state behind a chevron.
                if (w == null) setOpen(true);
            } catch (_e) {
                // Soft-fail — likely "no athlete profile yet". Nothing to render.
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, [athleteId]);

    const dirty = (() => {
        const cur = weight.trim() === "" ? null : parseFloat(weight);
        return cur !== serverValue;
    })();

    const save = async () => {
        const w = parseFloat(weight);
        if (Number.isNaN(w) || w <= 0 || w >= 1000) {
            toast.error("Enter a bodyweight between 1 and 999 lbs.");
            return;
        }
        setSaving(true);
        try {
            const params = athleteId ? { athlete_id: athleteId } : {};
            const r = await axios.put(
                `${API}/me/biometrics`,
                { weight_lbs: w },
                { params, withCredentials: true },
            );
            setServerValue(r.data?.weight_lbs ?? w);
            setUpdatedAt(r.data?.updated_at || new Date().toISOString());
            toast.success(
                `Bodyweight saved · ${w} lbs${athleteName ? ` · ${athleteName}` : ""}`,
            );
        } catch (e) {
            toast.error("Couldn't save bodyweight", {
                description: e?.response?.data?.detail,
            });
        } finally {
            setSaving(false);
        }
    };

    const ago = formatAgo(updatedAt);

    return (
        <div
            data-testid="bodyweight-card"
            className={`border border-zinc-800 bg-zinc-950/70 ${className}`}
        >
            <button
                type="button"
                data-testid="bodyweight-toggle"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900/60 transition-colors"
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <Scale className="w-4 h-4 text-brand-red flex-shrink-0" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                        Bodyweight
                    </span>
                    {!loading && (serverValue != null ? (
                        <span
                            data-testid="bodyweight-value"
                            className="font-display text-base tracking-wider text-white"
                        >
                            {serverValue}
                            <span className="text-zinc-400 text-xs ml-1">lbs</span>
                        </span>
                    ) : (
                        <span
                            data-testid="bodyweight-empty"
                            className="text-[10px] tracking-[0.2em] uppercase text-amber-400"
                        >
                            Not set
                        </span>
                    ))}
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-wider text-zinc-400 flex-shrink-0">
                    {ago && !open && <span className="hidden sm:inline">{ago}</span>}
                    {open ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                    )}
                </div>
            </button>
            {open && (
                <div className="px-4 pb-4 pt-1 border-t border-zinc-900">
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                        Used to calculate relative-strength progressions (e.g. push-up
                        targets at % of bodyweight). Update anytime — history is kept.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex items-center gap-2 flex-1">
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                min="1"
                                max="999"
                                data-testid="bodyweight-input"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && dirty) save();
                                }}
                                placeholder="175"
                                disabled={loading || saving}
                                className="w-32 bg-zinc-900 border border-zinc-800 px-3 py-2 text-base text-white focus:outline-none focus:border-brand-red transition-colors"
                            />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                                lbs
                            </span>
                        </div>
                        <button
                            type="button"
                            data-testid="bodyweight-save"
                            onClick={save}
                            disabled={saving || !dirty}
                            className="btn-tactical inline-flex disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4" />
                            )}
                            Save
                        </button>
                    </div>
                    {ago && (
                        <div
                            data-testid="bodyweight-updated"
                            className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2"
                        >
                            Last updated · {ago}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
